import { cn } from '@crm/ui';
import { Inbox, Info } from 'lucide-react';
import type { ReactNode } from 'react';

/**
 * Trilho horizontal do quadro: rola no celular, com cada coluna ocupando
 * quase a tela toda (snap), e vira grade fixa a partir do desktop.
 */
export function KanbanBoard({ children }: { children: ReactNode }) {
  return (
    <div className="-mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-3 sm:mx-0 sm:px-0">
      {children}
    </div>
  );
}

interface KanbanColumnProps {
  label: string;
  /** Uma linha explicando o que entra nesta coluna - opcional, nem todo quadro precisa. */
  hint?: string;
  count: number;
  /** Conteudo extra no cabecalho, abaixo do titulo - filtros da coluna, por exemplo. */
  extra?: ReactNode;
  onInfo?: () => void;
  /** Destaca a coluna como alvo do arraste em andamento. */
  highlighted?: boolean;
  onDragOver?: (event: React.DragEvent) => void;
  onDrop?: (event: React.DragEvent) => void;
  emptyLabel?: string;
  children: ReactNode;
}

/** Uma coluna do quadro, com cabecalho, contagem e a pilha de cartoes. */
export function KanbanColumn({
  label,
  hint,
  count,
  extra,
  onInfo,
  highlighted,
  onDragOver,
  onDrop,
  emptyLabel = 'Nada aqui',
  children,
}: KanbanColumnProps) {
  return (
    <section
      aria-label={label}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={cn(
        // Fora do celular a coluna acompanha a largura da tela, presa entre
        // um minimo que cabe o cartao e um teto que evita a faixa larga
        // demais em monitor grande.
        'flex w-[80vw] min-w-[17rem] shrink-0 snap-start flex-col overflow-hidden rounded-card border border-border-strong bg-muted/40 sm:w-72',
        highlighted && 'border-dashed border-primary/50',
      )}
    >
      {/* Barra de titulo: separa o nome da coluna da pilha de cartoes sem
          pesar - o cinza da coluna ja e o contraste. */}
      <header className="border-b border-border/60 bg-card/60 px-3 py-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1">
            <h2 className="min-w-0 truncate text-sm font-semibold tracking-tight">{label}</h2>
            {onInfo ? (
              <button
                type="button"
                aria-label={`Informações sobre ${label}`}
                className="shrink-0 rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
                onClick={onInfo}
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            ) : null}
          </div>
          <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-xs font-medium tabular-nums text-muted-foreground">
            {count}
          </span>
        </div>
        {hint ? (
          <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{hint}</p>
        ) : null}
        {extra}
      </header>

      <div className="space-y-2 p-2">
        {/* Coluna vazia diz que esta vazia: espaco em branco no meio do
            quadro passa por carregamento que nao terminou. */}
        {count === 0 ? (
          <div className="flex flex-col items-center gap-1.5 rounded-card border border-dashed border-border px-3 py-6 text-center text-muted-foreground">
            <Inbox className="h-5 w-5" />
            <p className="text-xs">{emptyLabel}</p>
          </div>
        ) : null}

        {children}
      </div>
    </section>
  );
}

interface KanbanCardProps {
  draggable?: boolean;
  onDragStart?: () => void;
  className?: string;
  children: ReactNode;
}

/** O cartao base - a moldura e igual em todo quadro, o miolo e de quem usa. */
export function KanbanCard({ draggable, onDragStart, className, children }: KanbanCardProps) {
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: arrastar e atalho de mouse; quem usa teclado ou toque move por um controle proprio dentro do cartao
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      className={cn(
        'space-y-2 rounded-md border border-border-strong bg-card p-3 shadow-sm transition-shadow hover:shadow-md',
        className,
      )}
    >
      {children}
    </div>
  );
}
